import { useApp } from 'contexts/app';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Icon,
    IconButton,
    ListSubheader,
    MenuItem,
    ResponsiveDialog,
    Select,
    Stack,
    styled,
    Text,
} from 'shell/components';

const SelectRubricDialog = memo(({ open, onClose, onClickAddRubric, onClickUseRubric }) => {
    const { rubrics } = useApp();
    const { t } = useTranslation('assignment');

    const [selected, setSelected] = useState('');

    const handleClickAdd = () => {
        onClickAddRubric();
        onClose();
    };

    const handleClickUse = () => {
        const rubric = rubrics.find(({ id }) => id === selected);
        onClickUseRubric(rubric);
        onClose();
    };

    useEffect(() => {
        if (!open) setSelected('');
    }, [open]);

    return (
        <CustomResponsiveDialog aria-labelledby={'dialog-title'} open={open} onClose={onClose}>
            <Stack spacing={8}>
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                    <Stack spacing={2}>
                        <Text id={'dialog-title'} component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t(
                                'dialog.select-rubric.title',
                                'Select a rubric template or create a rubric from scratch'
                            )}
                        </Text>
                        <Text>
                            {t(
                                'dialog.select-rubric.description',
                                'From the dropdown you can either select an existing rubric template as a starting point, or create a new rubric from scratch.'
                            )}
                        </Text>
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                <Select
                    label={t('dialog.select-rubric.select-template', 'Select a Rubric Template')}
                    labelId={'rubric-select'}
                    matchWidth
                    value={selected}
                    onChange={(e) => {
                        if (!e.target.value) return;
                        setSelected(e.target.value);
                    }}
                >
                    <Button
                        sx={{ p: 1, justifyContent: 'flex-start' }}
                        startIcon={<Icon kind={'add'} />}
                        variant={'borderless'}
                        onClick={handleClickAdd}
                    >
                        {t('dialog.select-rubric.create-new-rubric', 'Create a new rubric')}
                    </Button>
                    <ListSubheader>
                        <Text color={'light'} variant={'p3'}>
                            {t('dialog.select-rubric.rubric-templates', 'RUBRIC TEMPLATES')}
                        </Text>
                    </ListSubheader>
                    {rubrics?.map((rubric) => (
                        <MenuItem key={rubric.id} value={rubric.id}>
                            <Stack
                                sx={{ width: '100%', gap: '12px !important' }}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                            >
                                <Text noWrap>{rubric.rubric.title}</Text>
                                <Text sx={{ flexShrink: 0 }} color={'light'} component={'i'} align={'right'}>
                                    {t('dialog.select-rubric.update-time', 'Updated {{time}}', {
                                        time: t('common:ui.format-month-day', { val: rubric.modified_on }),
                                    })}
                                </Text>
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
                <Button sx={{ alignSelf: 'flex-end' }} onClick={handleClickUse}>
                    {t('dialog.select-rubric.use-template', 'Use Template')}
                </Button>
            </Stack>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiDialog-paper > .MuiBox-root': {
            width: '700px',
        },
    };
});

export default SelectRubricDialog;
